(() => {
  const all = (selector, root = document) => Array.from((root || document).querySelectorAll(selector))
  const html = document.documentElement

  const hideStorefrontChrome = () => {
    all("#dtf-sticky-cta, #dtf-section-dots, #dtf-mascot, #dtf-mascot-restore, #dtf-checkout-tap-overlay, .dtf-progress-wrap").forEach(element => {
      element.style.setProperty("display", "none", "important")
      element.setAttribute("aria-hidden", "true")
    })
  }

  hideStorefrontChrome()
  window.addEventListener("load", hideStorefrontChrome, { once: true })

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const saveData = Boolean(navigator.connection?.saveData)
  all("[data-gfr-hero-video]").forEach(video => {
    let started = false
    const start = () => {
      if (started) return
      started = true
      video.src = window.innerWidth < 750 ? video.dataset.mobileSrc : video.dataset.desktopSrc
      video.addEventListener("canplay", () => video.play().catch(() => {}), { once: true })
      video.load()
    }
    video.addEventListener("pointerdown", start, { once: true, passive: true })
    if (reducedMotion || saveData) return
    if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 1600 })
    else window.setTimeout(start, 600)
  })

  all("[data-gfr-reason-video]").forEach(video => {
    let started = false
    const start = () => {
      if (started) return
      started = true
      video.src = video.dataset.gfrVideoSrc
      video.addEventListener("canplay", () => video.play().catch(() => {}), { once: true })
      video.load()
    }
    video.addEventListener("pointerdown", start, { once: true, passive: true })
    if (reducedMotion || saveData) return
    if (!("IntersectionObserver" in window)) {
      window.setTimeout(start, 1200)
      return
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          start()
          video.play().catch(() => {})
        } else if (started) video.pause()
      })
    }, { rootMargin: "240px 0px", threshold: 0.01 })
    observer.observe(video)
  })

  all("[data-gfr-review-filter]").forEach(filter => {
    const wall = filter.closest(".gfr-proof")?.querySelector("[data-gfr-review-wall]")
    const buttons = all("[data-gfr-review-category]", filter)
    const select = filter.querySelector("[data-gfr-review-select]")
    const activate = category => {
      buttons.forEach(button => {
        const active = button.dataset.gfrReviewCategory === category
        button.classList.toggle("is-active", active)
        button.setAttribute("aria-pressed", String(active))
      })
      all(".gfr-tp-card", wall).forEach(card => {
        card.hidden = category !== "all" && card.dataset.category !== category
      })
      if (select) select.value = category
    }
    buttons.forEach(button => button.addEventListener("click", () => activate(button.dataset.gfrReviewCategory)))
    select?.addEventListener("change", () => activate(select.value))
  })

  all("[data-gfr-review-archive]").forEach(archive => {
    const grid = archive.querySelector("[data-gfr-review-grid]")
    const button = archive.querySelector("[data-gfr-review-more]")
    const loading = archive.querySelector("[data-gfr-review-loading]")
    const error = archive.querySelector("[data-gfr-review-error]")
    const status = archive.querySelector("[data-gfr-review-status]")
    const template = archive.parentElement?.querySelector("[data-gfr-review-template]")
    const initial = Number(archive.dataset.initial) || 20
    const step = Number(archive.dataset.step) || 10
    let reviews = []
    let visible = 0
    let loaded = false
    let loadingPromise = null

    const updateState = () => {
      if (button) button.hidden = !loaded || visible >= reviews.length
      if (status && loaded) status.textContent = `Showing ${visible} of ${reviews.length} customer reviews`
    }

    const appendReviews = count => {
      if (!grid || !template) return
      const fragment = document.createDocumentFragment()
      reviews.slice(visible, visible + count).forEach(review => {
        const card = template.content.firstElementChild.cloneNode(true)
        card.querySelector("[data-gfr-review-name]").textContent = review.name
        card.querySelector("[data-gfr-review-content]").textContent = review.content
        const time = card.querySelector("[data-gfr-review-date]")
        time.textContent = review.date
        time.dateTime = review.dateValue
        fragment.append(card)
      })
      grid.append(fragment)
      visible = Math.min(visible + count, reviews.length)
      updateState()
    }

    const load = () => {
      if (loadingPromise) return loadingPromise
      loading.hidden = false
      error.hidden = true
      archive.setAttribute("aria-busy", "true")
      loadingPromise = fetch(archive.dataset.source, { credentials: "same-origin" })
        .then(response => {
          if (!response.ok) throw new Error(`Review archive returned ${response.status}`)
          return response.json()
        })
        .then(data => {
          if (!Array.isArray(data) || !data.length) throw new Error("Review archive is empty")
          reviews = data
          loaded = true
          grid.replaceChildren()
          appendReviews(initial)
        })
        .catch(() => {
          error.hidden = false
        })
        .finally(() => {
          loading.hidden = true
          archive.removeAttribute("aria-busy")
        })
      return loadingPromise
    }

    button?.addEventListener("click", () => appendReviews(step))
    if (!("IntersectionObserver" in window)) {
      load()
      return
    }
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return
      observer.disconnect()
      load()
    }, { rootMargin: "1200px 0px", threshold: 0.01 })
    observer.observe(archive)
  })

  all("[data-gfr-faq]").forEach(faq => {
    all(".gfr-faq__item button", faq).forEach(button => {
      button.addEventListener("click", () => {
        const item = button.closest(".gfr-faq__item")
        const open = item.classList.toggle("is-open")
        button.setAttribute("aria-expanded", String(open))
      })
    })
  })

  all("[data-gfr-ugc]").forEach(section => {
    const row = section.querySelector("[data-gfr-ugc-row]")
    const slides = all(".gfr-ugc__slide", section)
    const ensureVideo = video => {
      if (!video || video.src) return
      video.src = video.dataset.gfrVideoSrc
      video.load()
    }
    const toggle = async slide => {
      const video = slide.querySelector("video")
      if (!video) return
      ensureVideo(video)
      const playing = !video.paused
      slides.forEach(other => {
        const otherVideo = other.querySelector("video")
        if (otherVideo && otherVideo !== video) otherVideo.pause()
        if (other !== slide) other.classList.remove("is-playing")
      })
      if (playing) {
        video.pause()
        slide.classList.remove("is-playing")
        return
      }
      video.muted = false
      try {
        await video.play()
        slide.classList.add("is-playing")
      } catch {
        video.muted = true
        await video.play().catch(() => {})
        slide.classList.add("is-playing")
      }
    }
    slides.forEach(slide => {
      const video = slide.querySelector("video")
      const button = slide.querySelector("[data-gfr-video-toggle]")
      if (video) {
        video.muted = true
        video.playsInline = true
        video.addEventListener("pause", () => slide.classList.remove("is-playing"))
        video.addEventListener("ended", () => slide.classList.remove("is-playing"))
      }
      button?.addEventListener("click", event => {
        event.stopPropagation()
        toggle(slide)
      })
      slide.addEventListener("click", () => toggle(slide))
    })
    const move = direction => {
      const slide = slides[0]
      if (!row || !slide) return
      row.scrollBy({ left: direction * (slide.getBoundingClientRect().width + 14), behavior: reducedMotion ? "auto" : "smooth" })
    }
    section.querySelector("[data-gfr-ugc-prev]")?.addEventListener("click", () => move(-1))
    section.querySelector("[data-gfr-ugc-next]")?.addEventListener("click", () => move(1))
  })

  const funnel = document.querySelector("[data-dtfs-free-sample]")
  let landingScroll = 0
  let restoring = false
  const syncFunnelPresentation = () => {
    if (!funnel) return
    const committed = Boolean(funnel.dataset.currentScreen && funnel.dataset.currentScreen !== "experience")
    const wasCommitted = html.classList.contains("fs-puppypad-committed")
    html.classList.toggle("fs-puppypad-committed", committed)
    if (!committed && wasCommitted && !restoring) {
      restoring = true
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: landingScroll, behavior: reducedMotion ? "auto" : "smooth" })
        restoring = false
      })
    }
  }

  if (funnel) {
    new MutationObserver(syncFunnelPresentation).observe(funnel, { attributes: true, attributeFilter: ["data-current-screen"] })
    syncFunnelPresentation()
  }

  const sticky = document.querySelector("[data-fs-sticky]")
  const updateSticky = () => {
    const question = document.querySelector("#dtfs-fs-question")
    const rect = question?.getBoundingClientRect()
    const questionVisible = Boolean(rect && rect.top < window.innerHeight - 72 && rect.bottom > 72)
    sticky?.classList.toggle("is-visible", window.scrollY > 640 && !questionVisible)
  }

  window.addEventListener("scroll", updateSticky, { passive: true })
  window.addEventListener("resize", updateSticky)
  updateSticky()

  document.addEventListener("click", event => {
    const cta = event.target.closest("[data-fs-funnel-cta]")
    if (cta) {
      event.preventDefault()
      document.querySelector("#dtfs-fs-question")?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
      return
    }
    if (event.target.closest('[data-answer="experience"]')) landingScroll = window.scrollY
  })
})()
